import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import {
  INav,
  useDeleteNavMutation,
  useGetNavsLazyQuery,
  useNavCreatedSubscription,
  useNavDeletedSubscription,
  useNavUpdatedSubscription
} from '../../../../generated/graphql';
import moment from 'moment';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { exportDataAsExcel } from '../../../../utils/fileUtiles';
import { canUserCreate } from '../../../../utils/rightUtils';
import { renderDate } from '../../../../utils/styleUtils';
import { Box } from '@mui/material';
import DenseTable from '../../../Common/Tables/DenseTable';
import { Colors } from '../../../../utils/theme';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import NavForm from './NavForm';
import InnerLoader from '../../../Common/InnerLoader';

interface IProps {
  stockId: string | undefined;
  stockName: string | undefined;
}

const NavTab: React.FunctionComponent<IProps> = ({ stockId, stockName }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [getNavs, { data, loading }] = useGetNavsLazyQuery({ fetchPolicy: 'no-cache' });
  const [allData, setAllData] = useState([]);
  const [fileteredData, setFileteredData] = useState([]);
  const [date, setDate] = useState<Date | null>(null);
  const [openNavForm, setOpenNavForm] = useState(false);
  const [nav, setNav] = useState<INav | null>(null);
  const [deleteNav] = useDeleteNavMutation();
  const canCreate = canUserCreate(state.user, Types.Stock);
  const createNavSubscription = useNavCreatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const updateNavSubscription = useNavUpdatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const deleteNavSubscription = useNavDeletedSubscription({
    variables: { stock_id: stockId || '' }
  });

  useEffect(() => {
    if (stockId) {
      getNavs({
        variables: {
          stock_id: parseInt(stockId as string)
        }
      });
    }
  }, [stockId]);

  useEffect(() => {
    if (data && data.result) {
      const parsedData = data.result.map((n) => {
        return {
          ...n,
          filter_date: new Date(parseInt(n?.created_date as string)).toISOString().slice(0, 10)
        };
      }) as never[];
      setAllData(parsedData);
      setFileteredData(parsedData);
    }
  }, [data]);

  useEffect(() => {
    if (createNavSubscription.data?.result) {
      addNav(createNavSubscription.data?.result);
    }
    if (updateNavSubscription.data?.result) {
      updateNav(updateNavSubscription.data?.result);
    }
    if (deleteNavSubscription.data?.result) {
      removeNav(deleteNavSubscription.data?.result);
    }
  }, [createNavSubscription.data, deleteNavSubscription.data, updateNavSubscription.data]);

  const sortDateColumn = (a: INav, b: INav, column: 'created' | 'start') => {
    const first = new Date(parseInt(column === 'created' ? a.created_date : a.start_date));
    const second = new Date(parseInt(column === 'created' ? b.created_date : b.start_date));
    return first.getTime() - second.getTime();
  };

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 82
    },
    {
      title: 'Date',
      field: 'created_date',
      width: 167,
      type: 'date',
      render: (rowData: INav) => renderDate(rowData.created_date),
      customSort: (a: INav, b: INav) => sortDateColumn(a, b, 'created')
    },
    {
      title: 'Date Start',
      field: 'start_date',
      width: 178,
      type: 'date',
      render: (rowData: INav) => renderDate(rowData.start_date),
      customSort: (a: INav, b: INav) => sortDateColumn(a, b, 'start')
    },
    {
      title: 'TA',
      field: 'ta',
      type: 'numeric',
      width: 138
    },
    {
      title: 'TL',
      width: 129,
      field: 'tl',
      type: 'numeric'
    },
    {
      title: 'NAV',
      field: 'nav',
      type: 'numeric',
      width: 133
    },
    {
      title: 'NAV EPRA',
      width: 99,
      field: 'nav_epra',
      type: 'numeric'
    },

    {
      title: 'NAV Epra History',
      width: 184,
      field: 'nav_epra_history',
      type: 'numeric'
    },
    {
      title: 'NRA',
      field: 'nra',
      width: 104
    },
    {
      title: 'NTA',
      width: 102,
      field: 'nta'
    },
    {
      title: 'NDV',
      field: 'ndv',
      width: 123
    },
    {
      title: 'Average',
      field: 'average',
      width: 172
    },
    {
      title: 'Remove',
      sorting: false,
      field: 'status.name',
      width: 71
    }
  ] as IHeadCell[];

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        // hack to prevent changing day due to time zones and using isoString
        input.setHours(12);
        const isoInput = input.toISOString().slice(0, 10);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setFileteredData(allData.filter((n) => moment(isoInput).isSame(n.filter_date)));
      }
    } else {
      setFileteredData(allData);
    }
    setDate(input);
  };

  const exportCurrentNavs = () => {
    const fileName = `${stockName}-Nav-${
      date ? `${moment(date).format('YYYY_MM_DD')}` : `${moment(new Date()).format('YYYY_MM_DD')}`
    }`;
    const csvData = (fileteredData as INav[]).map((n) => ({
      Date: moment(parseInt(n.created_date)).format('DD-MM-yy'),
      'Date Start': moment(parseInt(n.start_date)).format('DD-MM-yy'),
      TA: n.ta,
      TL: n.tl,
      NAV: n.nav,
      'NAV EPRA': n.nav_epra,
      'NAV EPRA HISTORY': n.nav_epra_history,
      NRA: n.nra,
      NTA: n.nta,
      NDV: n.ndv,
      Average: n.average
    }));
    exportDataAsExcel(csvData, fileName);
  };

  const handleClose = (saved: boolean, isNew?: boolean, nav?: INav) => {
    if (saved && nav) {
      if (isNew) {
        addNav(nav);
      } else {
        updateNav(nav);
      }
    }

    setNav(null);
    setOpenNavForm(false);
  };

  const updateNav = (nav: INav) => {
    const updatedNav = {
      ...nav,
      filter_date: new Date(parseInt(nav?.created_date as string)).toISOString().slice(0, 10)
    };
    const updated = allData.map((n) => ((n as INav).id === nav.id ? (updatedNav as never) : n));
    setAllData(updated);
    if (date) {
      const isoDate = date.toISOString().slice(0, 10);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setFileteredData(updated.filter((n) => moment(isoDate).isSame(n.filter_date)));
    } else {
      setFileteredData(updated);
    }
  };

  const addNav = (nav: INav) => {
    setAllData([nav as never, ...allData]);
    if (date) {
      const navDate = new Date(parseInt(nav.created_date)).toISOString().slice(0, 10);
      const isoDate = date.toISOString().slice(0, 10);
      if (moment(isoDate).isSame(navDate)) {
        setFileteredData([nav as never, ...fileteredData]);
      }
    } else {
      setFileteredData([nav as never, ...fileteredData]);
    }
  };

  const removeNav = (nav: INav) => {
    setAllData(allData.filter((n) => (n as INav).id !== nav.id));
    setFileteredData(fileteredData.filter((n) => (n as INav).id !== nav.id));
  };

  const handleDetails = (rowData: INav) => {
    setNav(rowData);
    setOpenNavForm(true);
  };

  const handleRemove = (rowData: INav) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete nav from ${renderDate(rowData.created_date)}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (nav: INav) => {
    dispatch({ type: Actions.HideConfirmation });
    deleteNav({
      variables: {
        id: nav.id
      },
      update() {
        removeNav(nav);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: `Nav from ${renderDate(nav.created_date)} deleted`
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  return (
    <Box>
      {loading ? (
        <InnerLoader />
      ) : (
        <Box className="print\:w-full" sx={{ pt: '25px' }}>
          <Box sx={{ position: 'absolute', pl: '25px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="navs-date-picker"
                label="Date"
                value={date}
                onChange={handleDateChange}
              />
            </LocalizationProvider>
          </Box>
          <DenseTable
            columns={columns}
            data={fileteredData}
            handleDetails={handleDetails}
            handleRemove={handleRemove}
            toolbarBackground={Colors.white}
            createButtonProps={{
              id: 'create-nav-button',
              onClick: () => setOpenNavForm(true),
              text: 'Create',
              disabled: !canCreate
            }}
            exportButtonProps={{
              id: 'export-navs-button',
              onClick: exportCurrentNavs
            }}
            maxHeight={600}
            pageSize={15}
          />
          {openNavForm && (
            <NavForm stockId={stockId as string} handleClose={handleClose} navInput={nav} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default NavTab;
